import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Polyline, Marker, Popup, Tooltip, Circle, useMap } from "react-leaflet";
import { Zap, Route, Lock } from "lucide-react";
import { findNetwork } from "../lib/chargerNetworks";
import { CARTO_LIGHT, CARTO_DARK } from "../lib/mapTiles";
import { tNL } from "../lib/journalI18n";

const ChargerHover = ({ name, operator, powerKw }) => {
  const nw = findNetwork(operator);
  return (
    <Tooltip direction="top" offset={[0, -12]} opacity={1}>
      <span style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 2px" }}>
        {nw?.logo && <img src={nw.logo} alt={nw.label} style={{ height: 22, maxWidth: 84, objectFit: "contain" }} />}
        <span>
          <strong style={{ display: "block", fontSize: 12, lineHeight: 1.3 }}>{nw ? nw.label : operator || name}</strong>
          <span style={{ fontSize: 11, color: "#6B7280" }}>{name}{powerKw ? ` · ${powerKw} kW` : ""}</span>
        </span>
      </span>
    </Tooltip>
  );
};
import L from "leaflet";
import { infoUrl } from "../lib/links";
import { API_BASE, api, formatApiError } from "../lib/api";
import { confirmDialog } from "./ConfirmHost";
import { haversineKm } from "../lib/geo";
import { toast } from "sonner";
import { PhotoPins } from "./PhotoPins";
import { powerColor, POWER_LEGEND } from "../lib/power";
import { DrivenRoute, TripProgressPanel } from "./RouteProgress";

const localToday = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const havKm = (a, b) => {
  const R = 6371;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLon = ((b[1] - a[1]) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos((a[0] * Math.PI) / 180) * Math.cos((b[0] * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const liveIcon = L.divIcon({
  className: "",
  html: `<div style="width:18px;height:18px;border-radius:50%;background:#3B82F6;border:3px solid white;animation:live-dot-pulse 2s infinite"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const chargeIcon = (kw) => L.divIcon({
  className: "",
  html: `<div class="map-pin" style="width:32px;height:32px;background:${powerColor(kw)};color:#fff"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const fuelIcon = L.divIcon({
  className: "",
  html: `<div class="map-pin" style="width:32px;height:32px;background:#E76F51;color:#fff"><svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="3" x2="15" y1="22" y2="22"/><line x1="4" x2="14" y1="9" y2="9"/><path d="M14 22V4a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v18"/><path d="M14 13h2a2 2 0 0 1 2 2v2a2 2 0 0 0 2 2a2 2 0 0 0 2-2V9.83a2 2 0 0 0-.59-1.42L18 5"/></svg></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const stayIcon = (nights = 1, w = { s: "nacht", p: "nachten" }) => L.divIcon({
  className: "",
  html: `<div style="display:flex;flex-direction:column;align-items:center"><span style="margin-bottom:2px;background:#3A6EA5;color:#fff;font:800 9px 'Figtree',sans-serif;letter-spacing:.04em;text-transform:uppercase;padding:1.5px 8px;border-radius:999px;border:1.5px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);white-space:nowrap">${nights} ${nights > 1 ? w.p : w.s}</span><div class="map-pin map-pin-stay" style="width:32px;height:32px"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 4v16"/><path d="M2 8h18a2 2 0 0 1 2 2v10"/><path d="M2 17h20"/><path d="M6 8v9"/></svg></div><div style="width:2.5px;height:9px;background:#3a6ea5;border-radius:0 0 2px 2px"></div></div>`,
  iconSize: [90, 60],
  iconAnchor: [45, 60],
});

const tonightStayIcon = (nights = 1, w = { s: "nacht", p: "nachten", tonight: "vannacht" }) => L.divIcon({
  className: "",
  html: `<div style="display:flex;flex-direction:column;align-items:center"><span style="margin-bottom:2px;background:#F59E0B;color:#0F172A;font:800 9px 'Figtree',sans-serif;letter-spacing:.04em;text-transform:uppercase;padding:1.5px 8px;border-radius:999px;border:1.5px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);white-space:nowrap">${w.tonight}${nights > 1 ? ` · ${nights} ${w.p}` : ""}</span><div style="position:relative"><div class="map-pin" style="width:36px;height:36px;background:#F59E0B;color:#0F172A;animation:live-dot-pulse 2s infinite"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 4v16"/><path d="M2 8h18a2 2 0 0 1 2 2v10"/><path d="M2 17h20"/><path d="M6 8v9"/></svg></div><span style="position:absolute;top:-4px;right:-6px;width:16px;height:16px;border-radius:50%;background:#0F172A;border:1.5px solid #fff;display:flex;align-items:center;justify-content:center"><svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 24 24" fill="#FDE68A" stroke="#FDE68A" stroke-width="1"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg></span></div><div style="width:2.5px;height:9px;background:#F59E0B;border-radius:0 0 2px 2px"></div></div>`,
  iconSize: [130, 63],
  iconAnchor: [65, 63],
});

const flagIcon = (color = "#E07A5F", label = "") => L.divIcon({
  className: "",
  html: `<div style="display:flex;flex-direction:column;align-items:center">${label ? `<span style="margin-bottom:2px;background:${color};color:#fff;font:800 9px 'Figtree',sans-serif;letter-spacing:.04em;text-transform:uppercase;padding:1.5px 8px;border-radius:999px;border:1.5px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);white-space:nowrap">${label}</span>` : ""}<div class="map-pin" style="width:34px;height:34px;background:${color};color:#fff"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" x2="4" y1="22" y2="15"/></svg></div></div>`,
  iconSize: label ? [150, 54] : [34, 34],
  iconAnchor: label ? [75, 37] : [17, 17],
});

const START_COLOR = "#16A34A";
const END_COLOR = "#DC2626";
const TURN_COLOR = "#E07A5F";

const ROAD_TYPE_META = {
  motorway: { color: "#DC2626", label: "Autosnelweg" },
  national: { color: "#F59E0B", label: "Nationale weg" },
  departmental: { color: "#16A34A", label: "Departementale weg" },
  local: { color: "#64748B", label: "Lokale weg" },
  ferry: { color: "#0EA5E9", label: "Ferry" },
};
const ROAD_TYPE_ORDER = ["motorway", "national", "departmental", "local", "ferry"];

const CHARGER_STATUS_COLOR = { AVAILABLE: "#2D6A4F", CHARGING: "#D97706", INOPERATIVE: "#DC2626" };
const CHARGER_STATUS_NL = { AVAILABLE: "Operationeel", CHARGING: "Bezet", INOPERATIVE: "Buiten dienst", UNKNOWN: "Status onbekend" };

const routeChargerIcon = (c) => L.divIcon({
  className: "",
  html: `<div style="position:relative;width:20px;height:20px;border-radius:50%;background:${powerColor(c.max_power_kw)};display:flex;align-items:center;justify-content:center;color:white;border:2px solid white;box-shadow:0 2px 5px rgba(0,0,0,.3)"><svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>${CHARGER_STATUS_COLOR[c.status] ? `<span style="position:absolute;top:-3px;right:-3px;width:9px;height:9px;border-radius:50%;background:${CHARGER_STATUS_COLOR[c.status]};border:1.5px solid white"></span>` : ""}</div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const FlyTo = ({ point }) => {
  const map = useMap();
  useEffect(() => {
    if (point) map.flyTo([point.lat, point.lon], 11, { duration: 1.2 });
  }, [point, map]);
  return null;
};

const FitBounds = ({ geometry }) => {
  const map = useMap();
  useEffect(() => {
    if (geometry && geometry.length > 1) {
      map.fitBounds(L.latLngBounds(geometry), { padding: [40, 40] });
    }
  }, [geometry, map]);
  return null;
};

const gmaps = (lat, lon) => `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`;

const fmtNL = (iso, locale = "nl-BE") => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString(locale, { day: "numeric", month: "short" }) : "");

const MAX_MAP_POINTS = 3500;

const downsampleGeo = (pts, max = MAX_MAP_POINTS) => {
  if (!pts || pts.length <= max) return pts || [];
  const step = (pts.length - 1) / (max - 1);
  const out = new Array(max);
  for (let i = 0; i < max; i++) out[i] = pts[Math.round(i * step)];
  return out;
};

const nearestIdx = (geometry, pt, from = 0) => {
  let best = from;
  let bestD = Infinity;
  for (let i = from; i < geometry.length; i++) {
    const d = (geometry[i][0] - pt.lat) ** 2 + (geometry[i][1] - pt.lon) ** 2;
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
};

const buildSegments = (trip, geometry) => {
  const allDays = trip.days || [];
  const driveDays = allDays.filter((d) => d.type !== "stay" && d.end?.lat != null);
  if (geometry.length < 2 || driveDays.length === 0) return null;
  const same = (a, b) => a && b && Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lon - b.lon) < 1e-6;
  let turnPos = -1;
  allDays.forEach((d, i) => { if (same(d.end, trip.destination)) turnPos = i; });
  const cum = [0];
  for (let i = 1; i < geometry.length; i++) cum.push(cum[i - 1] + havKm(geometry[i - 1], geometry[i]));
  const idxAtKm = (km, from) => {
    let i = from;
    while (i < cum.length - 1 && cum[i] < km) i++;
    return i;
  };
  const segments = [];
  let runKm = 0;
  let cursor = 0;
  driveDays.forEach((day, di) => {
    const startKm = day.route_start_km != null ? day.route_start_km : runKm;
    const endKm = startKm + (day.distance_km || 0);
    runKm = endKm;
    const endIdx = di === driveDays.length - 1 ? geometry.length - 1 : idxAtKm(endKm, cursor);
    if (endIdx <= cursor) return;
    const posInTrip = allDays.indexOf(day);
    segments.push({ day, positions: geometry.slice(cursor, endIdx + 1), isReturn: !!trip.round_trip && turnPos >= 0 && posInTrip > turnPos });
    cursor = endIdx;
  });
  return segments.length > 1 ? segments : null;
};

const computeEta = (trip, day, pos, geometry) => {
  if (!day || day.type === "stay" || !day.end || !day.start) return null;
  if (geometry.length < 2) return null;
  const startIdx = nearestIdx(geometry, day.start);
  const endIdx = nearestIdx(geometry, day.end, startIdx);
  if (endIdx <= startIdx) return null;
  const seg = geometry.slice(startIdx, endIdx + 1);
  const cur = nearestIdx(seg, pos);
  let remKm = 0;
  let totKm = 0;
  for (let i = 0; i < seg.length - 1; i++) {
    const d = havKm(seg[i], seg[i + 1]);
    totKm += d;
    if (i >= cur) remKm += d;
  }
  if (totKm <= 0) return null;
  const driveMin = (day.drive_time_hours || 0) * 60 * (remKm / totKm);
  const stopsAhead = (day.charging_stops || [])
    .filter((cs) => cs.lat)
    .map((cs) => ({ cs, idx: nearestIdx(seg, cs) }))
    .filter((s) => s.idx > cur)
    .sort((a, b) => a.idx - b.idx);
  const chargeMin = stopsAhead.reduce((s, x) => s + (x.cs.charge_minutes || 0), 0);
  let nextStop = null;
  if (stopsAhead.length) {
    let stopKm = 0;
    for (let i = cur; i < stopsAhead[0].idx && i < seg.length - 1; i++) stopKm += havKm(seg[i], seg[i + 1]);
    nextStop = { name: (stopsAhead[0].cs.name || "Laadstop").split(",")[0], km: Math.round(stopKm), minutes: stopsAhead[0].cs.charge_minutes };
  }
  const eta = new Date(Date.now() + (driveMin + chargeMin) * 60000);
  return {
    km: Math.round(remKm),
    minutes: Math.round(driveMin + chargeMin),
    chargeMin,
    nextStop,
    geoIdx: startIdx + cur,
    offKm: havKm(seg[cur], [pos.lat, pos.lon]),
    time: eta.toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" }),
    dest: day.end.name,
  };
};

const LivePosition = ({ pos, eta }) => (
  <Marker position={[pos.lat, pos.lon]} icon={liveIcon} zIndexOffset={2000}>
    <Tooltip direction="top" offset={[0, -12]} opacity={1}>
      <div style={{ minWidth: 150 }}>
        <strong>Jullie zijn hier</strong><br />
        {eta ? (
          eta.km < 1 ? (
            <>Vandaag aangekomen in {eta.dest} ✓</>
          ) : (
          <>
            Nog {eta.km} km naar {eta.dest}<br />
            Aankomst ±{eta.time} ({Math.floor(eta.minutes / 60)}u{String(eta.minutes % 60).padStart(2, "0")})
            {eta.chargeMin > 0 && <><br />incl. ±{eta.chargeMin} min laden</>}
            {eta.nextStop && <><br />Volgende laadstop: {eta.nextStop.name} · over {eta.nextStop.km} km</>}
            <br /><span style={{ opacity: 0.65, fontSize: 11 }}>o.b.v. gepland tempo voor de resterende km{eta.chargeMin > 0 ? " + laadtijd" : ""}</span>
          </>
          )
        ) : (
          <>Vandaag geen rijdag</>
        )}
      </div>
    </Tooltip>
  </Marker>
);

const SegmentTooltip = ({ seg, isRound }) => {
  const d = seg.day;
  return (
    <Tooltip sticky opacity={1}>
      <div style={{ minWidth: 180 }}>
        <strong>Dag {d.day_number} · {d.date}</strong>{isRound && <em style={{ marginLeft: 4 }}>({seg.isReturn ? "terugreis" : "heenreis"})</em>}<br />
        {d.start?.name} → {d.end?.name}<br />
        {d.distance_km} km · ±{d.drive_time_hours}u rijden · aankomst ~{d.arrival_soc}%<br />
        {d.weather && <>{d.weather.temp_max}°C · {d.weather.description}<br /></>}
        Laadstops: {(d.charging_stops || []).length}
        {d.stay && <> · Overnachting: {d.stay.type === "eigen boeking" && <Lock style={{ width: 11, height: 11, verticalAlign: -1, marginRight: 2, color: "#059669", display: "inline" }} />}{d.stay.name}</>}
      </div>
    </Tooltip>
  );
};

const escapeHtml = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const viaIcon = (name = "") => L.divIcon({
  className: "",
  iconSize: [140, 42], iconAnchor: [70, 31],
  html: `<div style="display:flex;flex-direction:column;align-items:center;width:140px">
    <span style="margin-bottom:2px;background:#fff;color:#1B263B;font:800 9px 'Figtree',sans-serif;letter-spacing:.04em;text-transform:uppercase;padding:1.5px 8px;border-radius:999px;border:1.5px solid #3A6EA5;box-shadow:0 2px 6px rgba(0,0,0,.3);white-space:nowrap">via &middot; ${escapeHtml(name)}</span>
    <div style="width:13px;height:13px;background:#fff;border:3px solid #3A6EA5;transform:rotate(45deg);box-shadow:0 1px 4px rgba(0,0,0,.35);margin-top:2px"></div>
  </div>`,
});

const viaDragIcon = L.divIcon({
  className: "",
  html: '<div style="width:16px;height:16px;border-radius:50%;background:#fff;border:3px solid #2D6A4F;box-shadow:0 1px 5px rgba(0,0,0,.45)"></div>',
  iconSize: [16, 16], iconAnchor: [8, 8],
});

export const TripMap = ({ trip, selectedPoint, dark, chargers = [], shareToken, progress, progressCorner = "bottom-right", editable = false, onRefresh, t, locale }) => {
  const tt = t || tNL;
  const loc = locale || "nl-BE";
  const fullGeometry = trip.route?.geometry;
  const geometry = useMemo(() => downsampleGeo(fullGeometry || []), [fullGeometry]);
  const geoScale = fullGeometry && fullGeometry.length > geometry.length ? (geometry.length - 1) / (fullGeometry.length - 1) : 1;
  const mediaUrl = (id) => `${API_BASE}/trips/${trip.id}/photos/${id}${shareToken ? `?share=${shareToken}` : ""}`;
  const days = trip.days || [];
  const today = localToday();
  const dragInfoRef = useRef(null);
  const onTrip = days.some((d) => d.date === today);
  const [livePos, setLivePos] = useState(null);
  useEffect(() => {
    if (!onTrip || !navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (p) => setLivePos({ lat: p.coords.latitude, lon: p.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, maximumAge: 10000 }
    );
    return () => navigator.geolocation.clearWatch(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onTrip]);
  const todayDay = days.find((d) => d.date === today);
  const eta = useMemo(() => (livePos ? computeEta(trip, todayDay, livePos, geometry) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [livePos, todayDay, geometry]);
  const trackUploadRef = useRef({ ts: 0, pos: null });
  useEffect(() => {
    if (!livePos || shareToken || !todayDay || todayDay.type === "stay" || !(todayDay.distance_km || 0) || todayDay.arrived_at) return;
    if (!eta || eta.offKm > 5) return;
    const u = trackUploadRef.current;
    if (Date.now() - u.ts < 30000) return;
    if (u.pos && havKm([u.pos.lat, u.pos.lon], [livePos.lat, livePos.lon]) < 0.1) return;
    trackUploadRef.current = { ts: Date.now(), pos: livePos };
    api.post(`/trips/${trip.id}/track`, { points: [{ lat: livePos.lat, lon: livePos.lon }] })
      .then(() => window.dispatchEvent(new Event("vr-progress-refresh")))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePos]);
  const liveDayAdjust = progress?.started && !progress.finished && progress.source !== "live"
    && eta && todayDay && !todayDay.arrived_at && eta.offKm <= 5 && (todayDay.distance_km || 0) > 0
    ? Math.max((todayDay.distance_km || 0) - eta.km, 0) : 0;
  const panelProgress = liveDayAdjust > 0.5 ? (() => {
    const driven = Math.min(progress.driven_km + liveDayAdjust, progress.total_km);
    const frac = Math.min(liveDayAdjust / (todayDay.distance_km || 1), 1);
    return {
      ...progress,
      driven_km: Math.round(driven * 10) / 10,
      percent: Math.round(Math.min(driven / (progress.total_km || 1), 1) * 100),
      drive_hours_done: Math.round((progress.drive_hours_done + (todayDay.drive_time_hours || 0) * frac) * 10) / 10,
    };
  })() : progress;
  const arrivedOnce = useRef(false);
  useEffect(() => {
    if (arrivedOnce.current || !livePos || !todayDay || todayDay.arrived_at) return;
    if (todayDay.type === "stay" || !(todayDay.distance_km || 0) || trip.is_owner === false || shareToken) return;
    const end = todayDay.end;
    if (!end || end.lat == null) return;
    if (havKm([livePos.lat, livePos.lon], [end.lat, end.lon]) > 1) return;
    arrivedOnce.current = true;
    api.post(`/trips/${trip.id}/days/${todayDay.day_number}/arrived`, { arrived: true })
      .then(() => { toast.success(`Aankomst in ${(end.name || "je bestemming").split(",")[0]} geregistreerd 🏁`); onRefresh?.(); })
      .catch(() => { arrivedOnce.current = false; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePos, todayDay]);
  const isRound = !!trip.round_trip;
  const segments = useMemo(() => buildSegments(trip, geometry), [trip, geometry]);
  const mapProgress = useMemo(() => {
    if (!progress || geoScale === 1 || !Number.isInteger(progress.position_geo_idx)) return progress;
    return { ...progress, position_geo_idx: Math.round(progress.position_geo_idx * geoScale) };
  }, [progress, geoScale]);
  const liveTrackPts = useMemo(() => (trip.live_track || []).map((p) => [p.lat, p.lon]), [trip.live_track]);
  const [roadTypes, setRoadTypes] = useState(null);
  const [showRoadTypes, setShowRoadTypes] = useState(false);
  const [roadTypesLoading, setRoadTypesLoading] = useState(false);
  const toggleRoadTypes = async () => {
    if (showRoadTypes) { setShowRoadTypes(false); return; }
    if (roadTypes) { setShowRoadTypes(true); return; }
    setRoadTypesLoading(true);
    try {
      const { data } = await api.get(`/trips/${trip.id}/road-types`);
      setRoadTypes(data);
      setShowRoadTypes(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Wegtypes konden niet geladen worden");
    }
    setRoadTypesLoading(false);
  };
  const progressActive = !!(progress?.started && progress.percent > 0);
  const baseOpacity = progressActive ? 0.35 : 0.85;
  const outColor = dark ? "#52B788" : "#2D6A4F";
  const backColor = dark ? "#FFB4A2" : "#E07A5F";
  const tiles = dark ? CARTO_DARK : CARTO_LIGHT;
  const center = geometry.length ? geometry[Math.floor(geometry.length / 2)] : [50.85, 4.35];
  const onRoad = progress?.started && !progress?.finished;
  const carPos = livePos || (onRoad ? progress.position : null);
  const carKm = onRoad ? progress.driven_km : null;
  const carLive = !!livePos || progress?.source === "live";

  const dragSession = useRef(0);
  const addViaAt = async (latlng) => {
    if (!editable) return;
    let name = null;
    try {
      const { data } = await api.get("/geocode/reverse", { params: { lat: latlng.lat, lon: latlng.lng } });
      name = data.name;
    } catch { /* noop */ }
    if (!name) { toast.error("Kon geen plaatsnaam vinden op dit punt"); return; }
    const short = name.split(",").slice(0, 2).join(",").trim();
    if (!(await confirmDialog(`Route via ${short} sturen? De route wordt opnieuw berekend — zonder extra overnachting of extra dag.`, { title: "Via-punt toevoegen", confirmLabel: "Rij hierlangs" }))) return;
    try {
      await api.put(`/trips/${trip.id}/waypoints`, { waypoints: [...(trip.waypoints || []), short] });
      toast.success(`Via-punt ${short} toegevoegd — je route wordt opnieuw berekend!`);
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const routeDragHandlers = editable ? {
    mousedown: (e) => {
      const map = e.target?._map;
      if (!map) return;
      L.DomEvent.stop(e.originalEvent);
      map.dragging.disable();
      dragSession.current = Date.now();
      const mk = L.marker(e.latlng, { icon: viaDragIcon, zIndexOffset: 3000, interactive: false }).addTo(map);
      const chip = dragInfoRef.current;
      if (chip) { chip.style.display = "block"; chip.textContent = "Versleep en laat los — de route rijdt dan via dat punt"; }
      const move = (ev) => { mk.setLatLng(ev.latlng); dragSession.current = Date.now(); };
      const up = (ev) => {
        map.off("mousemove", move);
        map.off("mouseup", up);
        map.dragging.enable();
        map.removeLayer(mk);
        if (chip) chip.style.display = "none";
        dragSession.current = Date.now();
        addViaAt(ev.latlng);
      };
      map.on("mousemove", move);
      map.on("mouseup", up);
    },
    click: (e) => {
      if (Date.now() - dragSession.current < 600) return;
      addViaAt(e.latlng);
    },
  } : {};

  return (
    <div className="relative w-full h-full">
      <div ref={dragInfoRef} data-testid="drag-distance-chip" style={{ display: "none" }}
        className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] rounded-full bg-[#0F172A]/90 text-white text-xs font-mono font-semibold px-4 py-2 shadow-lg pointer-events-none whitespace-nowrap" />
      <MapContainer center={center} zoom={7} scrollWheelZoom preferCanvas className="w-full h-full" data-testid="trip-map">
        <TileLayer url={tiles} attribution='&copy; <a href="https://www.openstreetmap.org/">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>' />
        <FitBounds geometry={geometry} />
        <FlyTo point={selectedPoint} />
        {(trip.avoid_zones || []).map((z, i) => (
          <Circle key={`avoid-${i}`} center={[z.lat, z.lon]} radius={15000}
            pathOptions={{ color: "#DC2626", fillColor: "#DC2626", fillOpacity: 0.08, weight: 1.5, dashArray: "6 6" }}>
            <Tooltip sticky opacity={1}>
              <strong>Vermeden zone:</strong> {z.name.replace("omleiding ", "")}
            </Tooltip>
          </Circle>
        ))}
        {showRoadTypes && roadTypes ? (
          roadTypes.segments.map((s, i) => {
            const meta = ROAD_TYPE_META[s.cls] || ROAD_TYPE_META.local;
            return (
              <Polyline
                key={`rt-${i}`}
                positions={downsampleGeo(s.geometry, 1500)}
                pathOptions={{ color: meta.color, weight: 5, opacity: 0.9, dashArray: s.cls === "ferry" ? "6 8" : null }}
                eventHandlers={{
                  mouseover: (e) => e.target.setStyle({ weight: 8, opacity: 1 }),
                  mouseout: (e) => e.target.setStyle({ weight: 5, opacity: 0.9 }),
                  ...routeDragHandlers,
                }}
              >
                <Tooltip sticky opacity={1}>
                  <strong>{s.label}</strong><br />
                  <span style={{ color: meta.color, fontWeight: 700 }}>{meta.label}</span> · {s.km} km
                </Tooltip>
              </Polyline>
            );
          })
        ) : segments ? (
          segments.map((seg, i) => (
            <Polyline
              key={`seg-${i}`}
              positions={seg.positions}
              pathOptions={{ color: seg.isReturn ? backColor : outColor, weight: 5, opacity: baseOpacity, dashArray: seg.isReturn ? "10 8" : null }}
              eventHandlers={{
                mouseover: (e) => e.target.setStyle({ weight: 8, opacity: 1 }),
                mouseout: (e) => e.target.setStyle({ weight: 5, opacity: baseOpacity }),
                ...routeDragHandlers,
              }}
            >
              <SegmentTooltip seg={seg} isRound={isRound} />
            </Polyline>
          ))
        ) : (
          geometry.length > 1 && <Polyline positions={geometry} pathOptions={{ color: outColor, weight: 5, opacity: baseOpacity }} eventHandlers={routeDragHandlers} />
        )}
        {progress?.started && (
          <DrivenRoute geometry={geometry} progress={mapProgress}
            liveOverride={livePos && eta && eta.offKm <= 5 ? { pos: livePos, idx: eta.geoIdx } : null} />
        )}
        {(() => {
          const lastDay = [...days].reverse().find((d) => (d.end || {}).lat != null);
          const endPt = lastDay?.end || (isRound ? trip.origin : trip.destination);
          const near = (a, b) => a && b && a.lat != null && b.lat != null && havKm([a.lat, a.lon], [b.lat, b.lon]) <= 2;
          const originIsEnd = near(trip.origin, endPt);
          const destIsEnd = near(trip.destination, endPt);
          const startDate = days[0]?.date;
          const endDate = lastDay?.date;
          const turnDays = days.filter((d) => d.end?.lat != null && near(d.end, trip.destination));
          const turnArrive = turnDays[0]?.date;
          const turnDepart = days[days.indexOf(turnDays[turnDays.length - 1]) + 1]?.date;
          const originLabel = originIsEnd
            ? [startDate && `${tt("depart")} ${fmtNL(startDate, loc)}`, endDate && endDate !== startDate && `${tt("homeWord")} ${fmtNL(endDate, loc)}`].filter(Boolean).join(" · ")
            : startDate ? `${tt("depart")} ${fmtNL(startDate, loc)}` : "";
          const endLabel = endDate ? `${tt("arrivalWord")} ${fmtNL(endDate, loc)}` : "";
          const turnLabel = turnArrive
            ? [`${tt("arrivalWord")} ${fmtNL(turnArrive, loc)}`, turnDepart && turnDepart !== turnArrive && `${tt("depart")} ${fmtNL(turnDepart, loc)}`].filter(Boolean).join(" · ")
            : "";
          return (
            <>
              {trip.origin && (
                <Marker position={[trip.origin.lat, trip.origin.lon]} icon={flagIcon(START_COLOR, originLabel)} zIndexOffset={1000}>
                  <Popup>
                    <strong>{originIsEnd ? "Vertrek- & eindpunt" : "Vertrekpunt"}</strong><br />
                    {trip.origin.full_name || trip.origin.name}<br />
                    <a href={gmaps(trip.origin.lat, trip.origin.lon)} target="_blank" rel="noreferrer">Open in Google Maps</a>
                  </Popup>
                </Marker>
              )}
              {endPt && !originIsEnd && (
                <Marker position={[endPt.lat, endPt.lon]} icon={flagIcon(END_COLOR, endLabel)} zIndexOffset={1000}>
                  <Popup>
                    <strong>Eindpunt</strong><br />
                    {endPt.full_name || endPt.name}<br />
                    <a href={gmaps(endPt.lat, endPt.lon)} target="_blank" rel="noreferrer">Open in Google Maps</a>
                    {destIsEnd && <><br /><a href={infoUrl((endPt.name || "").split(",")[0], "bezienswaardigheden info")} target="_blank" rel="noreferrer">Meer over deze plaats</a></>}
                  </Popup>
                </Marker>
              )}
              {trip.destination && !destIsEnd && !near(trip.destination, trip.origin) && (
                <Marker position={[trip.destination.lat, trip.destination.lon]} icon={flagIcon(TURN_COLOR, turnLabel)} zIndexOffset={1000}>
                  <Popup>
                    <strong>{isRound ? "Bestemming (keerpunt)" : "Bestemming"}</strong><br />
                    {trip.destination.full_name || trip.destination.name}<br />
                    <a href={gmaps(trip.destination.lat, trip.destination.lon)} target="_blank" rel="noreferrer">Open in Google Maps</a><br />
                    <a href={infoUrl((trip.destination.name || "").split(",")[0], "bezienswaardigheden info")} target="_blank" rel="noreferrer">Meer over deze plaats</a>
                  </Popup>
                </Marker>
              )}
            </>
          );
        })()}
        {liveTrackPts.length > 1 && (
          <Polyline positions={liveTrackPts} pathOptions={{ color: "#F59E0B", weight: 3, opacity: 0.55, dashArray: "6 8" }} />
        )}

        {days.map((d) =>
          (d.charging_stops || []).map((cs, i) =>
            cs.lat && cs.lon ? (
              <Marker key={`c-${d.day_number}-${i}`} position={[cs.lat, cs.lon]} icon={cs.fuel ? fuelIcon : chargeIcon(cs.max_power_kw)}>
                <ChargerHover name={cs.name} operator={cs.operator} powerKw={cs.fuel ? null : cs.max_power_kw} />
                <Popup>
                  <strong>{cs.name}</strong><br />
                  {cs.fuel ? (
                    <>Tankstop · ±{cs.charge_minutes || 10} min<br /></>
                  ) : (
                    <>
                      {cs.operator && <>{cs.operator}{cs.preferred_network ? " ★" : ""}<br /></>}
                      {cs.max_power_kw ? `${cs.max_power_kw} kW` : ""} · aankomst ~{cs.arrival_soc}%<br />
                      Laadtijd ±{cs.charge_minutes} min<br />
                    </>
                  )}
                  {(() => {
                    const dayStartKm = d.route_start_km != null ? d.route_start_km : days.filter((x) => x.type !== "stay" && x.day_number < d.day_number).reduce((a, x) => a + (x.distance_km || 0), 0);
                    const stopTripKm = cs.km_from_day_start != null ? dayStartKm + cs.km_from_day_start : null;
                    const delta = stopTripKm != null && carKm != null ? stopTripKm - carKm : null;
                    const hav = carPos ? havKm([carPos.lat, carPos.lon], [cs.lat, cs.lon]) : null;
                    return (
                      <>
                        {cs.km_from_day_start != null && (
                          <span data-testid="stop-km-from-start">Vanaf start dag {d.day_number}: na {Math.round(cs.km_from_day_start)} km{stopTripKm != null ? ` (${Math.round(stopTripKm)} km in de hele route)` : ""}<br /></span>
                        )}
                        {delta != null ? (
                          <span data-testid="stop-km-from-car" style={{ color: delta > 0.5 ? "#2D6A4F" : "#6B7280", fontWeight: 600 }}>
                            {delta > 0.5 ? `Vanaf de wagen${carLive ? " (live)" : ""}: nog ~${Math.round(delta)} km rijden` : "Al gepasseerd ✓"}<br />
                          </span>
                        ) : hav != null ? (
                          <span data-testid="stop-km-from-car" style={{ color: "#2D6A4F", fontWeight: 600 }}>
                            Vanaf de wagen{carLive ? " (live)" : ""}: ~{Math.round(hav)} km hemelsbreed<br />
                          </span>
                        ) : null}
                      </>
                    );
                  })()}
                  <a href={gmaps(cs.lat, cs.lon)} target="_blank" rel="noreferrer">Open in Google Maps</a>
                </Popup>
              </Marker>
            ) : null
          )
        )}
        {chargers.map((c) => (
          <Marker key={c.id} position={[c.coordinates.lat, c.coordinates.lon]} icon={routeChargerIcon(c)}>
            <ChargerHover name={c.name} operator={c.operator} powerKw={c.max_power_kw} />
            <Popup>
              <strong>{c.name}</strong><br />
              {c.operator && <>{c.operator}<br /></>}
              {c.max_power_kw ? `${c.max_power_kw} kW` : "vermogen onbekend"} · {CHARGER_STATUS_NL[c.status] || c.status}<br />
              {c.num_points ? <>{c.num_points} laadpunt{c.num_points === 1 ? "" : "en"}<br /></> : null}
              {c.usage_cost && <><em>{c.usage_cost}</em><br /></>}
              {(c.evses?.[0]?.connectors || []).map((con) => `${con.standard} (${con.power_kw} kW)`).join(", ")}
            </Popup>
          </Marker>
        ))}
        {(() => {
          const out = [];
          let lastPt = null;
          days.slice(0, -1).forEach((d) => {
            const pt = d.end?.lat != null ? d.end : lastPt;
            if (d.end?.lat != null) lastPt = d.end;
            if (!pt) return;
            const prev = out[out.length - 1];
            if (prev && Math.abs(prev.lat - pt.lat) < 5e-4 && Math.abs(prev.lon - pt.lon) < 5e-4) {
              prev.toDay = d.day_number;
              if (!prev.stay && d.stay) prev.stay = d.stay;
              return;
            }
            out.push({ lat: pt.lat, lon: pt.lon, name: pt.name, fromDay: d.day_number, toDay: d.day_number, stay: d.stay, date: d.date });
          });
          const todayNum = days.find((d) => d.date === today)?.day_number;
          const moveStay = async (s, latlng) => {
            if (dragInfoRef.current) dragInfoRef.current.style.display = "none";
            if (!(await confirmDialog(`Overnachting van dag ${s.fromDay === s.toDay ? s.fromDay : `${s.fromDay}–${s.toDay}`} hierheen verplaatsen? De route wordt opnieuw berekend.`, { title: "Overnachting verplaatsen", confirmLabel: "Verplaatsen" }))) {
              onRefresh?.();
              return;
            }
            try {
              const { data } = await api.post(`/trips/${trip.id}/days/${s.fromDay}/move-overnight`, { lat: latlng.lat, lon: latlng.lng });
              toast.success(`Overnachting verplaatst naar ${data.location} — route wordt herberekend`);
            } catch (err) {
              toast.error(formatApiError(err.response?.data?.detail));
            }
            onRefresh?.();
          };
          const showDragInfo = (s, latlng) => {
            const el = dragInfoRef.current;
            if (!el) return;
            const parts = [];
            const dayObj = days.find((d) => d.day_number === s.fromDay);
            const startPt = dayObj?.start;
            if (startPt?.lat != null) {
              parts.push(`dag ${s.fromDay}: ~${Math.round(haversineKm({ lat: startPt.lat, lon: startPt.lon }, { lat: latlng.lat, lon: latlng.lng }) * 1.25)} km`);
            }
            const nextDrive = days.find((d) => d.day_number > s.toDay && (d.distance_km || 0) > 0 && d.end?.lat != null);
            if (nextDrive) {
              parts.push(`dag ${nextDrive.day_number}: ~${Math.round(haversineKm({ lat: latlng.lat, lon: latlng.lng }, { lat: nextDrive.end.lat, lon: nextDrive.end.lon }) * 1.25)} km`);
            }
            el.textContent = parts.join("  ·  ");
            el.style.display = parts.length ? "block" : "none";
          };
          const deleteStay = async (s) => {
            const nights = s.toDay - s.fromDay + 1;
            const place = (s.name || "deze plek").split(",")[0];
            if (!(await confirmDialog(`Overnachting in ${place}${nights > 1 ? ` (${nights} nachten)` : ""} schrappen? Je rijdt dan door zonder deze stop — de planning wordt opnieuw berekend en je trip wordt korter.`, { title: "Overnachting schrappen", confirmLabel: "Schrappen", destructive: true }))) return;
            try {
              const { data } = await api.delete(`/trips/${trip.id}/days/${s.fromDay}/overnight`);
              toast.success(`Overnachting geschrapt${data?.note ? ` (${data.note})` : ""} — planning wordt opnieuw berekend!`);
            } catch (err) {
              toast.error(formatApiError(err.response?.data?.detail));
            }
            onRefresh?.();
          };
          return out.map((s) => {
            const tonight = todayNum != null && s.fromDay <= todayNum && s.toDay >= todayNum;
            const canDrag = editable && s.date && s.date >= today;
            const nights = s.toDay - s.fromDay + 1;
            return (
              <Marker key={`s-${s.fromDay}`} position={[s.lat, s.lon]} icon={tonight ? tonightStayIcon(nights, { s: tt("nightWord"), p: tt("nightsWord"), tonight: tt("tonightWord") }) : stayIcon(nights, { s: tt("nightWord"), p: tt("nightsWord") })}
                zIndexOffset={tonight ? 900 : 500} draggable={canDrag}
                eventHandlers={canDrag ? {
                  drag: (e) => showDragInfo(s, e.target.getLatLng()),
                  dragend: (e) => moveStay(s, e.target.getLatLng()),
                } : undefined}>
                <Popup>
                  <strong data-testid={`map-stay-popup-title-${s.fromDay}`}>{tonight ? "Vannacht slapen jullie hier — dag" : "Overnachting dag"} {s.fromDay === s.toDay ? s.fromDay : `${s.fromDay}–${s.toDay}`}{(() => {
                    const toDate = days.find((x) => x.day_number === s.toDay)?.date;
                    const from = fmtNL(s.date);
                    if (!from) return "";
                    return ` · ${from}${toDate && toDate !== s.date ? ` – ${fmtNL(toDate)}` : ""}`;
                  })()}:</strong> {s.name}
                  {s.stay && <><br />{s.stay.type === "eigen boeking" && <span data-testid={`map-stay-lock-${s.fromDay}`} title="Eigen boeking — definitief bevestigd"><Lock style={{ width: 11, height: 11, verticalAlign: -1, marginRight: 3, color: "#059669", display: "inline" }} /></span>}{s.stay.name} ({s.stay.type})</>}
                  <br /><a href={gmaps(s.lat, s.lon)} target="_blank" rel="noreferrer">Open in Google Maps</a>
                  <br /><a href={infoUrl((s.name || "").split(",")[0], "bezienswaardigheden info")} target="_blank" rel="noreferrer">Meer over deze plaats</a>
                  {canDrag && <><br /><em>Tip: versleep deze pin om de overnachting te verplaatsen</em></>}
                  {canDrag && (
                    <><br />
                    <button type="button" data-testid={`map-delete-stay-${s.fromDay}`} onClick={() => deleteStay(s)}
                      style={{ marginTop: 6, color: "#DC2626", fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "1px solid #DC2626", borderRadius: 999, padding: "3px 10px", cursor: "pointer" }}>
                      Schrap deze overnachting
                    </button></>
                  )}
                </Popup>
              </Marker>
            );
          });
        })()}
        {(trip.waypoint_locations || []).map((w, i) => {
          const removeVia = async () => {
            const wps = [...(trip.waypoints || [])];
            let idx = wps.length === (trip.waypoint_locations || []).length ? i
              : wps.findIndex((x) => x.toLowerCase().includes((w.name || "").split(",")[0].toLowerCase()));
            if (idx < 0) idx = i;
            wps.splice(idx, 1);
            if (!(await confirmDialog(`Via-punt ${w.name} verwijderen? De route wordt opnieuw berekend.`, { title: "Via-punt verwijderen", confirmLabel: "Verwijderen", destructive: true }))) return;
            try {
              await api.put(`/trips/${trip.id}/waypoints`, { waypoints: wps });
              toast.success(`Via-punt ${w.name} verwijderd — je route wordt opnieuw berekend!`);
              onRefresh?.();
            } catch (err) {
              toast.error(formatApiError(err.response?.data?.detail));
            }
          };
          return (
            <Marker key={`via-${i}`} position={[w.lat, w.lon]} icon={viaIcon((w.name || "").split(",")[0])} zIndexOffset={650}>
              <Popup>
                <strong data-testid={`map-via-popup-${i}`}>Via-punt: {w.name}</strong>
                <br /><span style={{ fontSize: 11, color: "#64748b" }}>De route rijdt hierlangs — geen overnachting of extra dag.</span>
                <br /><a href={gmaps(w.lat, w.lon)} target="_blank" rel="noreferrer">Open in Google Maps</a>
                {editable && (
                  <><br />
                  <button type="button" data-testid={`map-remove-via-${i}`} onClick={removeVia}
                    style={{ marginTop: 6, color: "#DC2626", fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "1px solid #DC2626", borderRadius: 999, padding: "3px 10px", cursor: "pointer" }}>
                    Via-punt verwijderen
                  </button></>
                )}
              </Popup>
            </Marker>
          );
        })}
        <PhotoPins entries={trip.log_entries} mediaUrl={mediaUrl} />
        {livePos && <LivePosition pos={livePos} eta={eta} />}
      </MapContainer>
      {!shareToken && geometry.length > 1 && (
        <button data-testid="road-types-toggle" onClick={toggleRoadTypes}
          className={`absolute ${trip?.travel_mode !== "moto" ? "top-[52px]" : "top-3"} right-3 z-[1000] rounded-full px-4 py-2 text-xs font-bold shadow-lg flex items-center gap-1.5 active:scale-95 transition-[transform,background-color,color] duration-200 ${showRoadTypes ? "bg-[#0F172A] text-white" : "bg-white text-foreground hover:bg-secondary"}`}>
          <Route className={`w-3.5 h-3.5 ${roadTypesLoading ? "animate-pulse" : ""}`} />
          {roadTypesLoading ? "Wegtypes laden..." : "Wegtypes"}
        </button>
      )}
      <div className="absolute bottom-4 left-4 z-[1000] flex flex-col items-start gap-2">
      {eta && (
        <div data-testid="map-eta-bar" className="rounded-2xl bg-[#0F172A]/85 backdrop-blur text-white text-xs font-mono px-4 py-2 shadow-lg whitespace-nowrap">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-[#3B82F6] animate-pulse" />
            {eta.km < 1 ? "Vandaag aangekomen ✓" : `Nog ${eta.km} km · ±${eta.time}${eta.chargeMin > 0 ? ` · +${eta.chargeMin}m laden` : ""}`}
          </div>
          {eta.km >= 1 && eta.nextStop && (
            <div data-testid="eta-next-charge" className="flex items-center gap-1.5 mt-1 text-[#FDE68A]">
              <Zap className="w-3 h-3 shrink-0" />
              <span className="truncate max-w-[220px]">{eta.nextStop.name}</span>
              <span className="shrink-0">· {eta.nextStop.km} km{eta.nextStop.minutes ? ` · ±${eta.nextStop.minutes}m` : ""}</span>
            </div>
          )}
        </div>
      )}
      {(segments || (showRoadTypes && roadTypes) || days.some((d) => (d.charging_stops || []).length > 0) || chargers.length > 0) && (
        <div data-testid="map-legend" className="rounded-xl bg-card/90 backdrop-blur px-3.5 py-2.5 shadow-lg text-xs">
          {progressActive && (
            <div className="flex items-center gap-2" data-testid="legend-driven">
              <span className="inline-block w-6 h-1.5 rounded-full bg-[#16A34A]" />
              <span className="font-semibold">{tt("drivenAlready")}</span>
            </div>
          )}
          {showRoadTypes && roadTypes && (
            <div data-testid="road-types-legend" className={progressActive ? "mt-1.5 pt-1.5 border-t border-border" : ""}>
              <p className="font-bold text-[10px] uppercase tracking-wide text-muted-foreground">Wegtypes</p>
              {ROAD_TYPE_ORDER.filter((c) => roadTypes.stats?.[c]).map((c) => (
                <div key={c} className="flex items-center gap-2 mt-1" data-testid={`road-type-legend-${c}`}>
                  <span className={`inline-block w-6 ${c === "ferry" ? "border-t-2 border-dashed" : "h-1.5 rounded-full"}`}
                    style={c === "ferry" ? { borderColor: ROAD_TYPE_META[c].color } : { backgroundColor: ROAD_TYPE_META[c].color }} />
                  <span className="font-semibold">{ROAD_TYPE_META[c].label}</span>
                  <span className="ml-auto pl-3 font-mono text-[10px] text-muted-foreground">{Math.round(roadTypes.stats[c].km)} km · {roadTypes.stats[c].pct}%</span>
                </div>
              ))}
            </div>
          )}
          {segments && !showRoadTypes && (
            <>
              <div className={`flex items-center gap-2 ${progressActive ? "mt-1.5" : ""}`}>
                <span className="inline-block w-6 h-1 rounded-full" style={{ backgroundColor: outColor }} />
                <span className="font-semibold">{isRound ? tt("outboundLeg") : tt("kind_route")}</span>
              </div>
              {isRound && (
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="inline-block w-6 border-t-2 border-dashed" style={{ borderColor: backColor }} />
                  <span className="font-semibold">{tt("returnLeg")}</span>
                </div>
              )}
            </>
          )}
          <div className="flex items-center gap-3 mt-1.5" data-testid="legend-start-end">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-full border border-white/60" style={{ backgroundColor: START_COLOR }} />
              <span className="font-semibold">{tt("startPoint")}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-full border border-white/60" style={{ backgroundColor: END_COLOR }} />
              <span className="font-semibold">{tt("endPoint")}</span>
            </span>
          </div>
          {(trip.avoid_zones || []).length > 0 && (
            <div className="flex items-center gap-2 mt-1.5" data-testid="legend-avoid-zone">
              <span className="inline-block w-3.5 h-3.5 rounded-full border border-dashed border-[#DC2626] bg-[#DC2626]/10" />
              <span className="font-semibold">{tt("avoidZone")}</span>
            </div>
          )}
          {(trip.live_track || []).length > 1 && (
            <div className="flex items-center gap-2 mt-1.5" data-testid="legend-driven-track">
              <span className="inline-block w-6 border-t-2 border-dashed border-[#F59E0B]" />
              <span className="font-semibold">{tt("realDriven")}</span>
            </div>
          )}
          {trip?.travel_mode !== "moto" && (
          <div className="flex items-center gap-2 mt-1.5" data-testid="legend-power">
            <span className="font-semibold text-[10px]">{tt("chargePower")}</span>
            {POWER_LEGEND.map((p) => (
              <span key={p.label} className="flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
                <span className="text-[9px] font-mono font-semibold">{p.label}</span>
              </span>
            ))}
            <span className="text-[9px] text-muted-foreground">kW</span>
          </div>
          )}
          {segments && !showRoadTypes && <p className="text-[10px] text-muted-foreground mt-1.5">{tt("hoverDayInfo")}</p>}
          {showRoadTypes && roadTypes && <p className="text-[10px] text-muted-foreground mt-1.5">Beweeg over de route voor wegnamen</p>}
        </div>
      )}
      </div>
      {progress?.started && <TripProgressPanel progress={panelProgress} overlay corner={progressCorner} moto={trip?.travel_mode === "moto"} {...(t ? { t } : {})} {...(locale ? { locale } : {})} />}
    </div>
  );
};
